import React, { useCallback } from "react";
import { useOnboardingStore } from "../../store/onboardingStore";
import { validateStep } from "../../utils/onboardingValidation";
import { OnboardingStepper } from "./OnboardingStepper";
import styles from "./OnboardingContainer.module.css";

interface OnboardingContainerProps {
    children: React.ReactNode;
    onSuccess?: (applicationId: string) => void;
    onSubmit?: () => Promise<void>;
    /** Override the top-left "Back to Login" action (e.g. cancel an admin-initiated onboarding). */
    onBack?: () => void;
    /** Label for the top-left back link (defaults to "Back to Login"). */
    backLabel?: string;
}

const STEPS = [
    { number: 1, title: "Business Info" },
    { number: 2, title: "Contact Info" },
    { number: 3, title: "Documents" },
    { number: 4, title: "ACH Setup" },
    { number: 5, title: "Signature" },
];

export const OnboardingContainer: React.FC<OnboardingContainerProps> = ({ children, onSuccess, onSubmit, onBack, backLabel }) => {
    const {
        currentStep,
        formData,
        uploadedFiles,
        errors,
        isSubmitting,
        successMessage,
        applicationId,
        nextStep,
        previousStep,
        goToStep,
        setError,
        clearErrors,
        resetForm,
    } = useOnboardingStore();

    const isFirstStep = currentStep === 1;
    const isLastStep = currentStep === STEPS.length;

    const canProceedToNextStep = useCallback(() => {
        const newErrors = validateStep(currentStep, formData, uploadedFiles.length);

        if (newErrors.size > 0) {
            const firstError = Array.from(newErrors.keys())[0];
            setError(firstError, Array.from(newErrors.values())[0]);
            return false;
        }

        clearErrors();
        return true;
    }, [currentStep, formData, uploadedFiles, setError, clearErrors]);

    const handleNextStep = useCallback(async () => {
        if (canProceedToNextStep()) {
            if (isLastStep && onSubmit) {
                await onSubmit();
            } else {
                nextStep();
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        }
    }, [canProceedToNextStep, nextStep, isLastStep, onSubmit]);

    const handlePreviousStep = useCallback(() => {
        clearErrors();
        previousStep();
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [clearErrors, previousStep]);

    const handleStepClick = useCallback(
        (stepNumber: number) => {
            if (stepNumber < currentStep) {
                clearErrors();
                goToStep(stepNumber);
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        },
        [currentStep, clearErrors, goToStep],
    );

    const handleBackToLogin = useCallback(() => {
        if (onBack) {
            onBack();
            return;
        }
        // Clear all persisted data before navigating to login
        resetForm();
        // Navigate to login page
        window.location.href = "/login";
    }, [resetForm, onBack]);

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                {/* Back to Login Link */}
                <button
                    onClick={handleBackToLogin}
                    className={styles.backLink}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                    <span>←</span>
                    <span>{backLabel ?? "Back to Login"}</span>
                </button>

                {/* Stepper */}
                <div className={styles.stepperContainer}>
                    <OnboardingStepper steps={STEPS} currentStep={currentStep} onStepClick={handleStepClick} />
                </div>

                {/* Form Card */}
                <div className={styles.formCard}>
                    {/* Form Content */}
                    <div className={styles.formContent}>{children}</div>

                    {/* Navigation Bar */}
                    <div className={styles.navigationBar}>
                        <button
                            onClick={handlePreviousStep}
                            disabled={isFirstStep || isSubmitting}
                            className={styles.buttonBack}
                        >
                            Back
                        </button>

                        <span className={styles.stepIndicator}>
                            Step {currentStep} of {STEPS.length}
                        </span>

                        <div className={styles.buttonGroup}>
                            {isLastStep ? (
                                <button
                                    onClick={handleNextStep}
                                    disabled={isSubmitting}
                                    className={styles.buttonSubmit}
                                >
                                    {isSubmitting ? "Submitting..." : "Submit Application"}
                                </button>
                            ) : (
                                <button
                                    onClick={handleNextStep}
                                    disabled={isSubmitting}
                                    className={styles.buttonContinue}
                                >
                                    Continue
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
